import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, createRouter } from '@tanstack/react-router'
import { SpritesPage } from '@/features/sprites/SpritesPage'

import { NotFound, RootLayout, RouteError } from '@/routes/__root'
import { ACTION_DETAIL_ROUTE_ID, ActionDetailRoutePage, validateActionDetailSearch } from '@/routes/actions.$id'
import { ACTIONS_ROUTE_ID, ActionsRoutePage, validateActionsSearch } from '@/routes/actions.index'
import { ANNOTATE_ROUTE_ID, AnnotatePage, validateAnnotateSearch } from '@/routes/annotate.$kind.$uid'
import { BackgroundRoutePage, validateBackgroundSearch } from '@/routes/backgrounds.$uid'
import { BackgroundsRoutePage, validateBackgroundsSearch } from '@/routes/backgrounds.index'
import { HomePage } from '@/routes/index'
import { JobsPage, validateJobsSearch } from '@/routes/jobs'
import { LibraryPage, validateLibrarySearch } from '@/routes/library'
import { PuppetsRoutePage, validatePuppetsSearch } from '@/routes/puppets'
import { SettingsIndexPage, validateSettingsSearch } from '@/routes/settings.index'
import { SettingsVocabularyPage, validateVocabularySearch } from '@/routes/settings.vocabulary'
import { SKEL_EDITOR_ROUTE_ID, SkelEditorPage, validateSkelEditorSearch } from '@/routes/skel.$id'
import { SKEL_INDEX_ROUTE_ID, SkelIndexPage, validateSkelIndexSearch } from '@/routes/skel.index'
import { SKEL_NEW_ROUTE_ID, SkelNewPage, validateSkelNewSearch } from '@/routes/skel.new'
import { SKEL_SPLIT_ROUTE_ID, SkelSplitPage, validateSkelSplitSearch } from '@/routes/skel.split'
import { StoryPage, validateStorySearch } from '@/routes/stories.$uid'
import { StoriesIndexPage, validateStoriesSearch } from '@/routes/stories.index'
import { StoryNewPage, validateStoryNewSearch } from '@/routes/stories.new'

/**
 * Code-based route tree (docs/BUILD-OVERRIDES.md: no file-route codegen).
 * Paths and search params follow §3.2; each route file owns its component
 * and `validateSearch`, this file only wires them.
 *
 * Adding a route in a later phase: list it in `docs/ROUTES.md`; the phase
 * integrator adds the `createRoute` here. Loaders (prefetch first page +
 * facets, doc + vocab) are added by the same integrator using
 * `context.queryClient`.
 */

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

/** Puppets (docs/SKEL-STORY.md P8): every catalog asset as a puppet, plus the parent's own. */
const puppetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/puppets',
  component: PuppetsRoutePage,
  validateSearch: validatePuppetsSearch,
})

const spritesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sprites',
  component: SpritesPage,
})

/**
 * Backgrounds: every stage, its places (via the annotator), its living scenery and its details.
 * A layout with an INDEX child (`/backgrounds/`, the list) and a splat child (`/backgrounds/$`, one
 * background; uids contain `/`). Flat, the splat would also match `/backgrounds` (an empty uid is
 * deeper, so it wins); an index segment beats a splat.
 */
const backgroundsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backgrounds',
})

const backgroundsRoute = createRoute({
  getParentRoute: () => backgroundsLayoutRoute,
  path: '/',
  component: BackgroundsRoutePage,
  validateSearch: validateBackgroundsSearch,
})

const backgroundRoute = createRoute({
  getParentRoute: () => backgroundsLayoutRoute,
  path: '$',
  component: BackgroundRoutePage,
  validateSearch: validateBackgroundSearch,
})

/** Actions + world rules (docs/SKEL-STORY.md R7, §11.7): the dynamic action registry. */
const actionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ACTIONS_ROUTE_ID,
  component: ActionsRoutePage,
  validateSearch: validateActionsSearch,
})

const actionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ACTION_DETAIL_ROUTE_ID,
  component: ActionDetailRoutePage,
  validateSearch: validateActionDetailSearch,
})

const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/library',
  component: LibraryPage,
  validateSearch: validateLibrarySearch,
})

/** `/annotate/$kind/<uid…>` — the uid is a splat because uids contain `/`. */
const annotateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ANNOTATE_ROUTE_ID,
  component: AnnotatePage,
  validateSearch: validateAnnotateSearch,
})

const storiesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stories',
  component: StoriesIndexPage,
  validateSearch: validateStoriesSearch,
})

const storiesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stories/new',
  component: StoryNewPage,
  validateSearch: validateStoryNewSearch,
})

const storyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stories/$uid',
  component: StoryPage,
  validateSearch: validateStorySearch,
})

const settingsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsIndexPage,
  validateSearch: validateSettingsSearch,
})

const settingsVocabularyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/vocabulary',
  component: SettingsVocabularyPage,
  validateSearch: validateVocabularySearch,
})

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs',
  component: JobsPage,
  validateSearch: validateJobsSearch,
})

/** Skeleton studio (docs/SKEL-EDITOR.md §5.4): home, parts-sheet splitter, editor. */
const skelIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SKEL_INDEX_ROUTE_ID,
  component: SkelIndexPage,
  validateSearch: validateSkelIndexSearch,
})

const skelNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SKEL_NEW_ROUTE_ID,
  component: SkelNewPage,
  validateSearch: validateSkelNewSearch,
})

/** Split to bones: one picture of a character -> a puppet with bones (features/skel/imagerig). */
const skelSplitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SKEL_SPLIT_ROUTE_ID,
  component: SkelSplitPage,
  validateSearch: validateSkelSplitSearch,
})

const skelEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SKEL_EDITOR_ROUTE_ID,
  component: SkelEditorPage,
  validateSearch: validateSkelEditorSearch,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  puppetsRoute,
  spritesRoute,
  backgroundsLayoutRoute.addChildren([backgroundsRoute, backgroundRoute]),
  actionsRoute,
  actionDetailRoute,
  libraryRoute,
  annotateRoute,
  storiesIndexRoute,
  storiesNewRoute,
  storyRoute,
  settingsIndexRoute,
  settingsVocabularyRoute,
  jobsRoute,
  skelIndexRoute,
  skelNewRoute,
  skelSplitRoute,
  skelEditorRoute,
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    // §5: uids travel verbatim. Without this TanStack percent-encodes the ':'
    // in `char:animals/forest/fox`, so the address bar shows `char%3Aanimals/...`.
    pathParamsAllowedCharacters: [':'],
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: NotFound,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0, // TanStack Query owns staleness
    scrollRestoration: true,
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
